import React, { useState, useEffect, useCallback } from 'react';
import EntityForm from './EntityForm';
import RelationshipForm from './RelationshipForm';
import SpecializationForm from './SpecializationForm';
import UnionForm from './UnionForm';
import ReferencePanel from './ReferencePanel';
import DesignPalette from './DesignPalette';

const VisualEditor = ({ erdState, onSaveEvent }) => {
  const [activeTab, setActiveTab] = useState('entity');

  const [editingMode, setEditingMode] = useState({
    isEditing: false,
    componentType: null,
    originalComponent: null
  });

  // State for form persistence
  const [currentEntity, setCurrentEntity] = useState({
    name: '',
    isWeak: false,
    attributes: [{ name: '', isKey: false, isPartialKey: false, isMultivalued: false, isDerived: false, subAttributes: '' }]
  });

  const [currentRelationship, setCurrentRelationship] = useState({
    name: '',
    isIdentifying: false,
    participants: [{ entity: '', cardinality: '1', roleName: '', hasTotalParticipation: false }],
    attributes: [{ name: '', subAttributes: '' }]
  });

  const [currentSpecialization, setCurrentSpecialization] = useState({
    superEntity: '',
    isDisjoint: true,
    isTotal: false,
    subEntities: ['']
  });

  const [currentUnion, setCurrentUnion] = useState({
    subCategoryEntity: '',
    isTotal: false,
    superCategories: ['']
  });

  // Clear state function (called when Reset is clicked or when loading versions)
  const clearVisualEditorState = useCallback(() => {
    setCurrentEntity({
      name: '',
      isWeak: false,
      attributes: [{ name: '', isKey: false, isPartialKey: false, isMultivalued: false, isDerived: false, subAttributes: '' }]
    });
    setCurrentRelationship({
      name: '',
      isIdentifying: false,
      participants: [{ entity: '', cardinality: '1', roleName: '', hasTotalParticipation: false }],
      attributes: [{ name: '', subAttributes: '' }]
    });
    setCurrentSpecialization({
      superEntity: '',
      isDisjoint: true,
      isTotal: false,
      subEntities: ['']
    });
    setCurrentUnion({
      subCategoryEntity: '',
      isTotal: false,
      superCategories: ['']
    });
    setActiveTab('entity');
    setEditingMode({
      isEditing: false,
      componentType: null,
      originalComponent: null
    });
    localStorage.removeItem(`visualEditor_${erdState.username}`);
  }, [erdState.username]);

  // Save state to localStorage whenever forms change
  useEffect(() => {
    const visualEditorState = {
      activeTab,
      currentEntity,
      currentRelationship,
      currentSpecialization,
      currentUnion
    };
    localStorage.setItem(`visualEditor_${erdState.username}`, JSON.stringify(visualEditorState));
  }, [activeTab, currentEntity, currentRelationship, currentSpecialization, currentUnion, erdState.username]);

  // Load state from localStorage on component mount
  useEffect(() => {
    const savedState = localStorage.getItem(`visualEditor_${erdState.username}`);
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        setActiveTab(parsed.activeTab || 'entity');
        setCurrentEntity(parsed.currentEntity || {
          name: '',
          isWeak: false,
          attributes: [{ name: '', isKey: false, isPartialKey: false, isMultivalued: false, isDerived: false, subAttributes: '' }]
        });
        setCurrentRelationship(parsed.currentRelationship || {
          name: '',
          isIdentifying: false,
          participants: [{ entity: '', cardinality: '1', roleName: '', hasTotalParticipation: false }],
          attributes: [{ name: '', subAttributes: '' }]
        });
        setCurrentSpecialization(parsed.currentSpecialization || {
          superEntity: '',
          isDisjoint: true,
          isTotal: false,
          subEntities: ['']
        });
        setCurrentUnion(parsed.currentUnion || {
          subCategoryEntity: '',
          isTotal: false,
          superCategories: ['']
        });
      } catch (error) {
        console.error('Error loading visual editor state:', error);
      }
    }
  }, [erdState.username]);

  // Expose the clear function to the parent
  useEffect(() => {
    erdState.clearVisualEditorState = clearVisualEditorState;
  }, [clearVisualEditorState, erdState]);

  const renderContent = () => {
    switch (activeTab) {
      case 'entity':
        return (
          <EntityForm
            erdState={erdState}
            currentEntity={currentEntity}
            setCurrentEntity={setCurrentEntity}
            editingMode={editingMode}
            setEditingMode={setEditingMode}
            onSaveEvent={onSaveEvent}
          />
        );
      case 'relationship':
        return (
          <RelationshipForm
            erdState={erdState}
            currentRelationship={currentRelationship}
            setCurrentRelationship={setCurrentRelationship}
            editingMode={editingMode}
            setEditingMode={setEditingMode}
            onSaveEvent={onSaveEvent}

          />
        );
      case 'specialization':
        return (
          <SpecializationForm
            erdState={erdState}
            currentSpecialization={currentSpecialization}
            setCurrentSpecialization={setCurrentSpecialization}
            editingMode={editingMode}
            setEditingMode={setEditingMode}
            onSaveEvent={onSaveEvent}

          />
        );
      case 'union':
        return (
          <UnionForm
            erdState={erdState}
            currentUnion={currentUnion}
            setCurrentUnion={setCurrentUnion}
            editingMode={editingMode}
            setEditingMode={setEditingMode}
            onSaveEvent={onSaveEvent}

          />
        );
      case 'reference':
        return <ReferencePanel />;
      default:
        return (
          <EntityForm
            erdState={erdState}
            currentEntity={currentEntity}
            setCurrentEntity={setCurrentEntity}
          />
        );
    }
  };

  return (
    <div className="flex h-full min-w-0">
      {/* Side Palette - Fixed width but responsive */}
      <DesignPalette
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        availableEntities={erdState.availableEntities}
        erdState={erdState}
        setCurrentEntity={setCurrentEntity}
        setCurrentRelationship={setCurrentRelationship}
        setCurrentSpecialization={setCurrentSpecialization}
        setCurrentUnion={setCurrentUnion}
        currentEntity={currentEntity}
        currentRelationship={currentRelationship}
        currentSpecialization={currentSpecialization}
        currentUnion={currentUnion}
        setEditingMode={setEditingMode}
        onSaveEvent={onSaveEvent}
      />

      {/* Main Content Area - Responsive and flexible */}
      <div className="flex-1 p-3 sm:p-4 lg:p-6 overflow-y-auto min-w-0">
        <div className="max-w-full">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default VisualEditor;